import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Waves, Sparkles, Sun, Star } from "lucide-react";
import Countdown from "@/components/Countdown";
import InquiryForm from "@/components/InquiryForm";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fetchEventInfo, fetchPackages, fetchServices, fetchShahiSnanDates } from "@/lib/api";

const FAQ = [
  { q: "What exactly is the Simhastha Mahakumbh?", a: "The Simhastha Mahakumbh at Ujjain is one of four Kumbh Melas — held once every twelve years when Jupiter enters Leo (Simha). It draws millions of pilgrims and sadhus to the banks of the Shipra for sacred ritual bathing under the gaze of Mahakaleshwar." },
  { q: "Why is Ujjain considered so significant?", a: "Ujjain is home to the Mahakaleshwar Jyotirlinga — one of the twelve most sacred manifestations of Shiva — and sits on the mystical Tropic of Cancer. Ancient astronomers marked their prime meridian here. It is a city of time, of Kaala, of Mahakaal himself." },
  { q: "When is the best time to attend?", a: "The main Mela spans April to June 2028. The Shahi Snan (royal bath) dates are the most spiritually potent — and the most crowded. Our concierge team designs journeys around your intent, whether ceremonial intensity or serene contemplation." },
  { q: "How do you protect our privacy and comfort?", a: "Our journeys are entirely private. Curated stays are located away from crowd density with dedicated transport, security detail on request, and priority access to ceremonies. Every detail is discreet." },
  { q: "Do you handle international pilgrims?", a: "Yes. A significant portion of our guests fly in from North America, the UK, UAE, Singapore and Australia. We coordinate visas, arrival protocols, and cultural orientation." },
];

const RIBBON = [
  "Bhasma Aarti at Mahakaleshwar",
  "Shahi Snan on the Shipra",
  "Presidential luxury tents",
  "Private pandits & acharyas",
  "Helicopter transfers",
  "24 / 7 concierge",
];

export default function Home() {
  const [event, setEvent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [snans, setSnans] = useState([]);
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    fetchEventInfo().then(setEvent).catch(() => {});
    fetchPackages().then(setPackages).catch(() => {});
    fetchServices().then((s) => { setServices(s); setActiveService(s[0]?.id); }).catch(() => {});
    fetchShahiSnanDates().then(setSnans).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-black">
        {/* Ambient glow backdrop — saffron halo behind the deity */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 55% at 78% 30%, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0.15) 30%, rgba(139,0,0,0.08) 55%, transparent 75%)",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 noise-overlay" aria-hidden />

        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-16 px-6 pt-32 pb-16 md:grid-cols-12 md:gap-12 md:px-12 md:pt-40 lg:gap-20">
          {/* Text column */}
          <div className="max-w-3xl fade-up md:col-span-7">
            <span className="overline text-primary">Once in twelve years · April – June 2028</span>
            <h1 className="mt-6 font-serif leading-[0.95] tracking-tight text-foreground text-5xl md:text-6xl lg:text-[5.5rem]">
              The <em className="italic text-primary">Sovereign</em> Pilgrimage.
              <br />
              Ujjain awaits.
            </h1>
            <p className="mt-8 max-w-xl font-sans-lux text-lg leading-relaxed text-foreground/80 md:text-xl">
              A private, deeply considered passage to Simhastha Mahakumbh at the seat of{" "}
              <span className="italic text-primary">Shri Mahakaleshwar</span> — curated with the discretion
              and grace of a boutique atelier.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/plan-your-visit">
                <Button
                  data-testid="hero-cta-primary"
                  className="rounded-none bg-primary px-10 py-7 text-primary-foreground hover:bg-primary/90"
                >
                  <span className="overline">Curate Your Journey</span>
                  <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/packages">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="ghost"
                  className="rounded-none border hairline px-10 py-7 text-foreground hover:bg-white/5"
                >
                  <span className="overline">View Packages</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Mahakaleshwar Temple image column */}
          <div className="relative md:col-span-5">
            <div className="relative mx-auto w-full max-w-md overflow-hidden border hairline md:max-w-none" style={{ aspectRatio: "1000 / 618" }}>
              <img
                src="https://static.wixstatic.com/media/6642a4_922258e938284da591d4b71707f69d14~mv2.jpg/v1/fill/w_1000,h_618,al_c,q_85,usm_0.66_1.00_0.01/6642a4_922258e938284da591d4b71707f69d14~mv2.jpg"
                alt="Shri Mahakaleshwar Jyotirlinga, Ujjain"
                className="h-full w-full object-contain"
              />
              {/* Corner ornaments */}
              <div className="pointer-events-none absolute left-3 top-3 h-6 w-6 border-l border-t border-primary/60" />
              <div className="pointer-events-none absolute right-3 top-3 h-6 w-6 border-r border-t border-primary/60" />
              <div className="pointer-events-none absolute left-3 bottom-3 h-6 w-6 border-l border-b border-primary/60" />
              <div className="pointer-events-none absolute right-3 bottom-3 h-6 w-6 border-r border-b border-primary/60" />
            </div>
            <div className="mt-6 text-center md:text-left">
              <p className="overline text-primary">Presiding Deity</p>
              <p className="mt-2 font-serif text-2xl italic text-foreground md:text-3xl">
                Shri Mahakaleshwar Jyotirlinga
              </p>
              <p className="mt-1 font-sans-lux text-xs text-foreground/60">
                One of twelve most sacred manifestations of Shiva · Ujjain
              </p>
            </div>

            {/* Live Darshan */}
            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                  </span>
                  <span className="overline text-primary">Live Darshan</span>
                </div>
                <a
                  href="https://www.youtube.com/watch?v=djAqGUJEvuc"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-live-video-external"
                  className="overline text-foreground/60 transition-colors hover:text-primary"
                >
                  Open on YouTube ↗
                </a>
              </div>
              <div className="relative w-full overflow-hidden border hairline" style={{ aspectRatio: "16 / 9" }}>
                <iframe
                  data-testid="hero-live-video"
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube.com/embed/djAqGUJEvuc?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1"
                  title="Live darshan from Shri Mahakaleshwar, Ujjain"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="mt-3 font-sans-lux text-xs text-foreground/50">
                Streaming from the sanctum of Mahakaleshwar
              </p>
            </div>
          </div>
        </div>

        {/* Countdown row */}
        {event && (
          <div className="relative z-10 border-t hairline bg-black/60 backdrop-blur-md">
            <div className="mx-auto max-w-[1400px] px-6 py-8 md:px-12 md:py-10">
              <div className="mb-6 flex items-baseline justify-between">
                <span className="overline text-foreground/60">Countdown to Chaitra Purnima</span>
                <span className="overline text-primary">09 April 2028</span>
              </div>
              <Countdown targetIso={event.start_date} />
            </div>
          </div>
        )}
      </section>

      {/* RIBBON */}
      <section className="relative overflow-hidden border-y hairline bg-secondary/40 py-6">
        <div className="flex whitespace-nowrap marquee-track">
          {[...RIBBON, ...RIBBON, ...RIBBON].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif text-2xl italic text-foreground/70 md:text-3xl">
              {t} <span className="text-primary">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-40" data-testid="about-section">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-7">
            <span className="overline text-primary">The City of Mahakaal</span>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] md:text-6xl lg:text-7xl">
              Where <em className="italic text-primary">time</em> pauses to breathe,
              and the eternal stirs at dawn.
            </h2>
            <div className="mt-10 max-w-xl space-y-6 font-sans-lux text-lg leading-relaxed text-foreground/75">
              <p>
                Ujjain has been the beating heart of Indian civilisation for over two millennia — home to the
                Mahakaleshwar Jyotirlinga, the ancient meridian of astronomy, and the Shipra, whose waters cleanse lifetimes.
              </p>
              <p>
                Simhastha is not an event to attend. It is a passage to inhabit. And it deserves to be met with quietness,
                reverence, and the finest care.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-10 md:grid-cols-4">
              {[
                { k: "12yr", v: "Cycle" },
                { k: "≈8M", v: "Pilgrims" },
                { k: "5", v: "Shahi Snans" },
                { k: "2028", v: "The Year" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-4xl text-primary md:text-5xl">{s.k}</div>
                  <div className="overline mt-2 text-foreground/60">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative md:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden border hairline">
              <img
                src="https://images.unsplash.com/photo-1543934776-32d1cc654abb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzYWRodSUyMHNwaXJpdHVhbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjYzNDc1OXww&ixlib=rb-4.1.0&q=85"
                alt="Sadhu portrait"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 hidden max-w-[240px] border hairline bg-black/85 p-6 backdrop-blur md:block">
              <p className="font-serif italic text-xl leading-snug text-foreground/90">
                "The Shipra remembers every soul that has ever come home."
              </p>
              <p className="overline mt-4 text-primary">Elder Acharya · Ujjain</p>
            </div>
          </div>
        </div>
      </section>

      {/* SHAHI SNAN DATES */}
      {snans.length > 0 && (
        <section className="border-y hairline bg-black py-32 md:py-40" data-testid="shahi-snan-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
              <div>
                <span className="overline text-primary">Sacred Dates</span>
                <h2 className="mt-6 font-serif text-4xl leading-tight md:text-6xl">
                  The five royal baths of <em className="italic">Simhastha 2028</em>
                </h2>
              </div>
              <Link to="/plan-your-visit" className="overline text-primary hover:opacity-80">
                Reserve around a Snan →
              </Link>
            </div>

            <div className="mt-16 divide-y hairline border-y hairline">
              {snans.map((s, i) => {
                const d = new Date(s.date);
                return (
                  <div
                    key={s.date}
                    data-testid={`snan-row-${i}`}
                    className="group grid grid-cols-12 items-center gap-6 py-8 transition-colors hover:bg-secondary/30"
                  >
                    <div className="col-span-2 font-serif text-4xl text-primary md:text-6xl">
                      {String(d.getDate()).padStart(2, "0")}
                    </div>
                    <div className="col-span-3">
                      <div className="overline text-foreground/60">
                        {d.toLocaleString("en-US", { month: "long" })}
                      </div>
                      <div className="mt-1 font-sans-lux text-sm text-foreground/60">2028</div>
                    </div>
                    <div className="col-span-4 font-serif text-2xl italic md:text-3xl">{s.label}</div>
                    <div className="col-span-3 hidden font-sans-lux text-sm text-foreground/60 md:block">
                      {s.significance}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PACKAGES (Bento) */}
      {packages.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-40" data-testid="packages-section">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <div>
              <span className="overline text-primary">Curated Journeys</span>
              <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight md:text-6xl">
                Three ways to arrive. <em className="italic">One</em> to remember forever.
              </h2>
            </div>
            <Link to="/packages" className="overline text-primary hover:opacity-80">
              All packages →
            </Link>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3 md:grid-rows-2 lg:h-[720px]">
            {packages.map((p, i) => (
              <Link
                key={p.id}
                to={`/packages/${p.id}`}
                data-testid={`package-card-${p.id}`}
                className={`group relative overflow-hidden border hairline bg-secondary/50 transition-colors hover:border-primary/60 ${
                  i === 0 ? "md:col-span-2 md:row-span-2" : "md:col-span-1 md:row-span-1"
                }`}
              >
                <div className="absolute inset-0">
                  <img src={p.hero_image} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                </div>
                <div className="relative z-10 flex h-full min-h-[280px] flex-col justify-between p-8 md:p-10">
                  <div>
                    <span className="overline text-primary">{p.tagline}</span>
                    <h3 className={`mt-4 font-serif leading-none ${i === 0 ? "text-5xl md:text-7xl" : "text-3xl md:text-4xl"}`}>
                      {p.name}
                    </h3>
                  </div>
                  <div className="flex items-end justify-between gap-6">
                    <div>
                      <div className="overline text-foreground/60">{p.duration}</div>
                      <div className="mt-2 font-serif text-2xl text-primary">
                        from ₹{p.price_from.toLocaleString("en-IN")}
                      </div>
                    </div>
                    <ArrowUpRight className="text-primary transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" size={28} strokeWidth={1.25} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES */}
      {services.length > 0 && (
        <section className="border-y hairline bg-black py-32 md:py-40" data-testid="services-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <span className="overline text-primary">Concierge Services</span>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight md:text-6xl">
              A single team. Every detail. <em className="italic">Unseen</em>.
            </h2>

            <div className="mt-16 grid gap-12 md:grid-cols-12">
              <div className="md:col-span-6">
                <ul className="divide-y hairline border-y hairline">
                  {services.map((s) => (
                    <li
                      key={s.id}
                      data-testid={`service-item-${s.id}`}
                      onMouseEnter={() => setActiveService(s.id)}
                      onFocus={() => setActiveService(s.id)}
                      tabIndex={0}
                      className={`group flex cursor-pointer items-center justify-between py-6 transition-colors md:py-8 ${
                        activeService === s.id ? "text-primary" : "text-foreground/70 hover:text-foreground"
                      }`}
                    >
                      <span className="font-serif text-3xl md:text-4xl">{s.name}</span>
                      <ArrowUpRight
                        strokeWidth={1.25}
                        className={`transition-opacity ${activeService === s.id ? "opacity-100" : "opacity-30"}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-6">
                <div className="sticky top-32 border hairline bg-secondary/40 p-8 md:p-10">
                  {services.filter((s) => s.id === activeService).map((s) => (
                    <div key={s.id} className="fade-up">
                      <span className="overline text-primary">Signature</span>
                      <h3 className="mt-4 font-serif text-4xl leading-tight md:text-5xl">{s.name}</h3>
                      <p className="mt-6 font-sans-lux text-lg leading-relaxed text-foreground/80">
                        {s.description}
                      </p>
                      <div className="mt-10 flex flex-wrap gap-4">
                        {[Flame, Waves, Sparkles, Sun, Star].slice(0, 4).map((Icon, i) => (
                          <div key={i} className="flex h-14 w-14 items-center justify-center border hairline text-primary">
                            <Icon strokeWidth={1.25} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Journey Guide */}
      <section className="mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-40" data-testid="journey-section">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="overline text-primary">Your Journey</span>
            <h2 className="mt-6 font-serif text-4xl leading-tight md:text-5xl">
              Four thoughtful <em className="italic">steps</em>.
            </h2>
          </div>
          <div className="md:col-span-8">
            <ol className="divide-y hairline border-y hairline">
              {[
                { k: "01", t: "Share your intent", d: "Tell us the shape of your Simhastha — a serene retreat, an intense ceremonial arc, a multi-generational family passage." },
                { k: "02", t: "Concierge design", d: "A senior concierge composes a bespoke itinerary — dates, stays, ceremonies, discourses, transfers." },
                { k: "03", t: "Curate & confirm", d: "Refine every detail. We handle darshan permits, pandit assignments, dietary preferences, security detail." },
                { k: "04", t: "Arrive & surrender", d: "You are met at the airport. Everything is ready. Every ritual, waiting. You need only be present." },
              ].map((step) => (
                <li key={step.k} className="grid grid-cols-12 gap-6 py-10">
                  <div className="col-span-2 font-serif text-4xl text-primary md:text-5xl">{step.k}</div>
                  <div className="col-span-10">
                    <h3 className="font-serif text-2xl md:text-3xl">{step.t}</h3>
                    <p className="mt-3 max-w-2xl font-sans-lux leading-relaxed text-foreground/70">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="relative overflow-hidden border-y hairline bg-black py-32 md:py-40" data-testid="testimonials-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://images.pexels.com/photos/35171273/pexels-photo-35171273.jpeg)` }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative mx-auto max-w-[1200px] px-6 md:px-12">
          <span className="overline text-primary">A note from a pilgrim</span>
          <blockquote className="mt-8 font-serif text-3xl leading-tight text-foreground md:text-5xl lg:text-6xl">
            "We arrived expecting a crowd. We left with a <em className="italic text-primary">stillness</em> that has not
            left us since. Every ritual, every meal, every silence, curated by a hand we never had to see."
          </blockquote>
          <div className="mt-10 flex items-center gap-6">
            <div className="h-12 w-12 border hairline bg-primary/20" />
            <div>
              <p className="font-serif text-xl">Anaya & Vikram Mehta</p>
              <p className="overline text-foreground/60">London · Prayagraj 2025 Pilgrims</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1200px] px-6 py-32 md:px-12 md:py-40" data-testid="faq-section">
        <span className="overline text-primary">Considerations</span>
        <h2 className="mt-6 font-serif text-4xl leading-tight md:text-6xl">
          What our guests <em className="italic">quietly</em> ask.
        </h2>
        <Accordion type="single" collapsible className="mt-16 divide-y hairline border-y hairline">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="py-8 text-left font-serif text-2xl no-underline hover:text-primary md:text-3xl">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-8 font-sans-lux text-lg leading-relaxed text-foreground/70">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Inquiry CTA */}
      <section id="inquiry" className="border-t hairline bg-black py-32 md:py-40" data-testid="inquiry-section">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <div className="grid gap-16 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="overline text-primary">Begin</span>
              <h2 className="mt-6 font-serif text-4xl leading-tight md:text-6xl">
                Let us <em className="italic">compose</em> your Simhastha.
              </h2>
              <p className="mt-8 max-w-md font-sans-lux text-lg leading-relaxed text-foreground/70">
                Share the outline of your intent. A senior concierge will respond within 24 hours with a considered proposal.
              </p>
            </div>
            <div className="md:col-span-7">
              <InquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
